/*
 * Registers the StackSchematic themes with Backstage's NEW frontend system.
 *
 * ThemeBlueprint is exported by @backstage/plugin-app-react and is restricted
 * to the `app` plugin, so the module declares pluginId: 'app' - the same shape
 * the scaffolded nav module uses. This is a supported extension point; no
 * upstream source is modified.
 *
 * THESE REPLACE BACKSTAGE'S THEMES; THEY DO NOT SIT BESIDE THEM
 *
 * `name` is load-bearing, and it is the whole fix for a real bug. An extension
 * id is `kind:namespace/name`, the namespace being the module's pluginId, and a
 * module extension REPLACES a plugin extension of the same id. Backstage's own
 * themes are `ThemeBlueprint.make({ name: 'light' | 'dark' })` inside
 * @backstage/plugin-app, so they are `theme:app/light` and `theme:app/dark`.
 *
 * Named `stackschematic-light`, ours resolved to `theme:app/stackschematic-light`
 * - a different id, so both sets loaded and Backstage kept selecting its own.
 * stackschematic.dev served the stock green Backstage header while carrying a
 * full navy palette nobody ever saw, and the only way to see it was to go into
 * Settings and pick it by hand. Taking the built-in names replaces them, so the
 * product looks like itself on first paint.
 *
 * `theme.id` deliberately stays 'light' / 'dark' rather than matching the title.
 * The id is what the theme selector persists per user, so changing it would
 * strand anyone whose stored preference reads 'light' and silently drop them
 * back to whatever the app defaults to.
 *
 * The icons are the same two the defaults use, so the Settings picker looks
 * unchanged apart from the names.
 */
import { createFrontendModule, createTranslationMessages } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint, TranslationBlueprint } from '@backstage/plugin-app-react';
import { userSettingsTranslationRef } from '@backstage/plugin-user-settings/alpha';
import { UnifiedThemeProvider } from '@backstage/theme';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';
import { autoGuestSignInPage } from './signIn';
import { viewerGlobalCss } from './globalCss';
import { viewerVersionBadge } from './versionBadge';
import { controlPlaneSession } from './controlPlaneSession';
import { stackSchematicDark, stackSchematicLight } from './stackschematicTheme';

const lightTheme = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'StackSchematic Light',
      icon: <LightIcon />,
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={stackSchematicLight}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

const darkTheme = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'StackSchematic Dark',
      icon: <DarkIcon />,
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={stackSchematicDark}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

/*
 * De-Backstage the stock user-settings text.
 *
 * "Backstage Identity" (and the other Backstage-branded strings in the user-settings plugin) read as
 * the tool underneath rather than the product. They are translatable messages on the plugin's own
 * translation ref, so overriding them through TranslationBlueprint is a supported extension point —
 * no upstream source is touched, and only the keys named here change; the rest fall back to the
 * plugin's defaults. TranslationBlueprint, like ThemeBlueprint, is restricted to the `app` plugin,
 * which is why it belongs in this module. See docs/upstream/backstage internationalization.
 */
const userSettingsText = TranslationBlueprint.make({
  name: 'stackschematic-user-settings',
  params: {
    resource: createTranslationMessages({
      ref: userSettingsTranslationRef,
      messages: {
        'identityCard.title': 'Identity',
        'identityCard.noIdentityTitle': 'No identity',
        'emptyProviders.description':
          'You can add Authentication Providers, which let you use those providers to authenticate yourself.',
        'featureFlags.emptyFlags.description':
          'Feature Flags let plugins register features for users to opt into. You can use this to split ' +
          'out logic in your code for manual A/B testing, etc.',
      },
    }),
  },
});

export const stackSchematicThemeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    lightTheme,
    darkTheme,
    autoGuestSignInPage,
    viewerGlobalCss,
    viewerVersionBadge,
    controlPlaneSession,
    userSettingsText,
  ],
});
